import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { ChevronDownIcon } from 'lucide-react'
import { cn } from '../utils'
import { Button } from './button'
import { useState } from 'react'
import { useTranslation } from '@clofo/i18n'

type ComboboxProps = {
  items: { value: string | number; label: string }[]
  value: string | number | null | undefined
  onChange: (value: string | number) => void
  placeholder?: string
  noItemFoundText?: string
  disabled?: boolean
  className?: string
  isInDialog?: boolean
}

export const Combobox = ({
  items,
  value,
  onChange,
  placeholder,
  noItemFoundText,
  disabled = false,
  className,
  isInDialog = false,
}: ComboboxProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const placeholderText = placeholder ?? `${t('common.selectItem')}...`
  const noItemFound = noItemFoundText ?? t('common.noItemFound')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant='tertiary'
          role='combobox'
          aria-expanded={open}
          aria-label={placeholderText}
          className={cn(
            'font-normal text-foreground border border-border hover:border-primary focus-visible:border-primary w-full overflow-hidden flex items-center justify-between hover:bg-background bg-background pr-2 has-[>svg]:pr-2',
            className,
          )}
        >
          <span
            className={cn(
              'text-left flex-1 min-w-0 overflow-hidden text-ellipsis text-nowrap',
              value !== null && value !== undefined ? '' : 'text-secondary-foreground',
            )}
          >
            {value !== null && value !== undefined
              ? items.find(item => item.value === value)?.label
              : placeholderText}
          </span>
          <ChevronDownIcon className='size-5 opacity-80 text-muted-foreground' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='min-w-max p-0' isInDialog={isInDialog}>
        <Command
          filter={(value, search) => {
            const label = value.split('___')[0] ?? ''
            return label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder={placeholderText} />
          <CommandEmpty>{noItemFound}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              {items.map(item => (
                <CommandItem
                  key={item.value}
                  value={`${item.label}___${item.value}`}
                  onSelect={selectedValue => {
                    const selectedId = selectedValue.split('___')[1] ?? ''
                    onChange(selectedId === String(value ?? '') ? '' : item.value)
                    setOpen(false)
                  }}
                >
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
